"use client";
import React, { useCallback, useState } from "react";

import { PreviousPurchaseBlock } from "@/app/ec/product/[basePath]/purchase-history/previousPurchaseBlock";
import {
  ProductHistoryProducts,
  PurchaseHistoryModal,
} from "@/app/ec/product/[basePath]/purchase-history/purchaseHistoryModal";
import useLazyPurchaseHistory from "@/app/ec/product/[basePath]/purchase-history/useLazyPurchaseHistory";

interface PurchaseHistoryProps {
  /** Pre-formatted MM/DD/YYYY date shown in the trigger. Optional for `variant="button"`. */
  lastPurchaseDate?: string;
  mpId: number;
  /** "link" renders the "You purchased this item on…" / "Purchased on…" block (default);
   *  "button" renders a plain button trigger (shopping list). */
  variant?: "link" | "button";
  /** Trigger content for `variant="button"` (icon + label). */
  children?: React.ReactNode;
  buttonClassName?: string;
  /** Notified when the user opens the modal (analytics hooks etc.). */
  purchaseHistoryOpenedCallback?: () => void;
  useCompactDisplay?: boolean;
  labelClass?: string;
  isTopsortClickable?: boolean;
}

/**
 * Self-fetching Purchase History trigger + modal (RNG-1593).
 *
 * The order history and category tree are fetched lazily on the FIRST open of the modal —
 * never on page load — so pages that render the trigger (PDP, PLPs, reorder, reviews,
 * shopping list) pay nothing until the user actually asks for the modal. Consumers only
 * supply `mpId` (and a display date for the link variant); all fetching lives here.
 */
export function PurchaseHistory({
  lastPurchaseDate,
  mpId,
  variant = "link",
  children,
  buttonClassName,
  purchaseHistoryOpenedCallback,
  useCompactDisplay,
  labelClass,
  isTopsortClickable = false,
}: PurchaseHistoryProps) {
  const [showPurchaseHistory, setShowPurchaseHistory] = useState(false);
  // A failed load shows an error in the modal; closing and reopening it retries.
  const { purchaseHistory, categoriesTree, loadFailed, load } = useLazyPurchaseHistory(mpId);

  const openPurchaseHistory = useCallback(() => {
    setShowPurchaseHistory(true);
    purchaseHistoryOpenedCallback?.();
    void load();
  }, [load, purchaseHistoryOpenedCallback]);

  return (
    <React.Fragment>
      {variant === "button" ? (
        <button type="button" className={buttonClassName} onClick={openPurchaseHistory}>
          {children}
        </button>
      ) : (
        <PreviousPurchaseBlock
          onClick={openPurchaseHistory}
          lastPurchaseDate={lastPurchaseDate ?? ""}
          useCompactDisplay={useCompactDisplay}
          className={labelClass}
          isTopsortClickable={isTopsortClickable}
        />
      )}
      {showPurchaseHistory && (
        <PurchaseHistoryModal
          mpId={mpId}
          isVisible={showPurchaseHistory}
          closeCallback={() => setShowPurchaseHistory(false)}
          purchaseHistory={purchaseHistory}
          categoriesTree={categoriesTree}
          loadFailed={loadFailed}
        />
      )}
    </React.Fragment>
  );
}

/** Product header block of the Order History modal. The modern orders endpoint populates
 *  description (orders-owned) and brand/manufacturer/retail via the marketplace stitch. */
export interface ManufacturerProductInfo {
  defDescr: string | null;
  brandName: string | null;
  manName: string | null;
  pdpLink?: string | null;
  retailPrice: number | null;
}

/** Response of GET /orders/product-purchase-history (shape-compatible with the legacy Neo payload). */
export interface OrderHistoryResponse {
  ugrId: number;
  mpId?: number | null;
  lastPurchaseDate?: string | null;
  /** Total line-item purchases ("N Total Purchases"). */
  totalPurchases?: number;
  productHistoryProducts: ProductHistoryProducts[];
  manufacturerProduct: ManufacturerProductInfo | null;
  /** Always empty from the modern endpoint — the modal fetches live vendor options itself
   *  (getVendorOptions); kept for backward shape-compatibility with the Neo payload. */
  vendorOptions: unknown[];
}
